//#region ../src/visualization/three-dispose.ts
/** A three.js texture (by its `isTexture` flag, as three tests it). */
function isTexture(value) {
	return typeof value === "object" && value !== null && value.isTexture === true;
}
/**
* Traverse an Object3D and all its descendants, disposing GPU resources:
* geometries (Mesh), materials (Mesh + Sprite), and material textures.
*
* Does NOT remove root from its parent — callers handle that.
*
* @param root The Object3D tree root to clean up.
* @param opts Optional flags (e.g. skipGeometry for shared geometries).
*/
function disposeObject3D(root, opts) {
	const disposed = /* @__PURE__ */ new Set();
	root.traverse((object) => {
		if ("geometry" in object && object.geometry) {
			const geo = object.geometry;
			if (!opts?.skipGeometry && !disposed.has(geo)) {
				disposed.add(geo);
				geo.dispose();
			}
		}
		if (!opts?.skipMaterial && "material" in object) {
			const raw = object.material;
			const materials = Array.isArray(raw) ? raw : [raw];
			for (const mat of materials) {
				if (!mat || disposed.has(mat)) continue;
				disposed.add(mat);
				for (const value of Object.values(mat)) {
					if (!isTexture(value) || disposed.has(value)) continue;
					disposed.add(value);
					value.dispose();
				}
				mat.dispose();
			}
		}
	});
}
/**
* Remove meshes from a parent, dispose their GPU resources, and clear
* the array in-place.
*
* @param meshes Array of meshes (mutated — emptied after disposal).
* @param parent The Object3D to remove meshes from. Pass null/undefined to skip removal.
* @param opts   Optional flags (e.g. skipGeometry for shared geometries).
*/
function disposeMeshArray(meshes, parent, opts) {
	for (const mesh of meshes) {
		if (parent) parent.remove(mesh);
		disposeObject3D(mesh, opts);
	}
	meshes.length = 0;
}
//#endregion
export { disposeMeshArray, disposeObject3D };
